import React from 'react';
import SourceRow from './SourceRow';

const MONTH = { month: 'long', year: 'numeric' };
const DAY = { day: 'numeric', month: 'short', year: 'numeric' };

// companyLine is the sentence under a company's name.
//
// It states only what this data supports. The mock also showed a client/prospect
// badge and an open-role count, but app.companies.relationship is always 'client'
// and app.jobs.status is always 'unknown' (both KindConstant in internal/mapper's
// catalog), so neither could be rendered truthfully. Job start dates and activity
// dates ARE populated, so those are what appear.
export function companyLine(company) {
  const parts = [];
  parts.push(
    company.sources.length
      ? `${company.sources.length} source${company.sources.length > 1 ? 's' : ''}`
      : 'No sources yet'
  );
  if (company.last_job_start) {
    parts.push(`last role started ${new Date(company.last_job_start).toLocaleDateString('en-GB', MONTH)}`);
  }
  if (company.last_activity_at) {
    parts.push(`last contact ${new Date(company.last_activity_at).toLocaleDateString('en-GB', DAY)}`);
  }
  return parts.join(' · ');
}

export default function CompanyGroup({
  company, open, mode, selectedIds, busyKeys = new Set(),
  onToggleOpen, onToggleMonitoring, onToggleActive, onSelect, onRemove, onChangeType,
}) {
  const on = company.signals_on;
  const companyBusy = busyKeys.has(company.company_id);
  return (
    <div className={`rounded-lg border border-gray-200 overflow-hidden ${on ? 'bg-white' : 'bg-gray-50'}`}>
      <div className="flex items-center gap-3 w-full px-4 py-3">
        <button
          type="button"
          onClick={() => onToggleOpen(company)}
          aria-expanded={open}
          aria-label={`Expand ${company.name}`}
          className="flex-1 min-w-0 flex items-center gap-3 text-left"
        >
          <span className={`text-gray-400 text-[10px] transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
          <span className="flex-1 min-w-0">
            <span className={`block font-semibold text-sm ${on ? 'text-gray-900' : 'text-gray-500'}`}>
              {company.name}
              {/* A row the user just toggled is HELD on screen even once it stops
                  matching the filter, so the result of their own click is visible
                  instead of the row vanishing under them. It says which way it went,
                  because an unexplained row that no longer fits the filter is more
                  confusing than the disappearance was. */}
              {company.just_toggled && (
                <span className="ml-2 align-middle text-[10px] font-medium uppercase tracking-wider text-gray-400">
                  {on ? 'now watched' : 'no longer watched'}
                </span>
              )}
            </span>
            <span className="block text-xs text-gray-500 mt-0.5">{companyLine(company)}</span>
          </span>
        </button>
        {/* Labelled, not merely coloured: this switch gates every source beneath it. */}
        <span className="flex items-center gap-2 shrink-0">
          <label className="relative inline-block w-8 h-5">
            <input
              type="checkbox"
              className="absolute inset-0 opacity-0 cursor-pointer"
              aria-label={`Monitor ${company.name}`}
              checked={on}
              disabled={companyBusy}
              onChange={() => onToggleMonitoring(company, !on)}
            />
            <span className={`absolute inset-0 rounded-full transition-colors ${on ? 'bg-[#00C6AE]' : 'bg-gray-300'}`}>
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
            </span>
          </label>
          <span className={`text-xs w-20 text-right ${on ? 'text-teal-800 font-semibold' : 'text-gray-400'}`}>
            {on ? 'Monitoring' : 'Not watched'}
          </span>
        </span>
      </div>

      {open && (
        <div className="border-t border-gray-100 bg-gray-50/60">
          {!on && company.sources.length > 0 && (
            <p className="px-4 py-2.5 pl-9 text-xs text-gray-600 border-b border-gray-100">
              Monitoring is off, so nothing here is scanned. The sources are kept — switch the
              company back on and they resume.
            </p>
          )}
          {company.sources.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-gray-500">
              No sources for {company.name} yet.
            </p>
          ) : (
            company.sources.map((s) => (
              <SourceRow
                key={s.source_id}
                source={s}
                mode={mode}
                gated={!on}
                selected={selectedIds.has(s.source_id)}
                busy={busyKeys.has(s.source_id)}
                onToggleActive={onToggleActive}
                onSelect={onSelect}
                onRemove={onRemove}
                onChangeType={onChangeType}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
